import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useSearchParams } from 'react-router-dom';
import { 
  BookOpen, Search, Plus, FolderOpen, FileText, ChevronRight,
  Edit2, Trash2, Eye, Clock, User, Tag, Loader2, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function KnowledgeBase() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);
  const [newArticle, setNewArticle] = useState({
    title: '',
    content: '',
    category: 'general',
    tags: []
  });
  const [creating, setCreating] = useState(false);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchArticles();
  }, [token]);

  useEffect(() => {
    const search = searchParams.get('search');
    if (search) {
      setSearchTerm(search);
    }
  }, [searchParams]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/support/knowledge-base`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setArticles(data.articles || []);
        updateCategories(data.articles || []);
      }
    } catch (error) {
      console.error('Fetch articles error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateCategories = (articlesList) => {
    const categoryCounts = {};
    articlesList.forEach(article => {
      const cat = article.category || 'general';
      categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
    });
    
    const cats = [
      { id: 'all', name: 'All Articles', count: articlesList.length },
      ...Object.entries(categoryCounts).map(([id, count]) => ({
        id,
        name: id.charAt(0).toUpperCase() + id.slice(1),
        count
      }))
    ];
    setCategories(cats);
  };

  // Sample data as fallback
  const sampleCategories = [
    { id: 'all', name: 'All Articles', count: 24 },
    { id: 'technical', name: 'Technical', count: 8 },
    { id: 'billing', name: 'Billing', count: 5 },
    { id: 'installation', name: 'Installation', count: 6 },
    { id: 'troubleshooting', name: 'Troubleshooting', count: 3 },
    { id: 'faq', name: 'FAQ', count: 2 },
  ];

  const sampleArticles = [
    {
      id: 1,
      title: 'How to Activate Software License',
      content: 'This guide explains the step-by-step process to activate your software license...\n\n1. Open the application\n2. Go to Settings > License\n3. Enter your license key\n4. Click Activate\n\nIf you encounter issues, check your internet connection and firewall settings.',
      category: 'technical',
      tags: ['license', 'activation', 'getting-started'],
      views: 1245,
      author: 'Admin',
      created_at: '2025-05-15T10:00:00Z',
      updated_at: '2025-05-20T14:30:00Z'
    },
    {
      id: 2,
      title: 'Troubleshooting Installation Errors',
      content: 'Common installation errors and how to fix them...\n\nError 1001: Insufficient permissions\n- Run installer as administrator\n\nError 1002: Missing dependencies\n- Install .NET Framework 4.8 or higher',
      category: 'installation',
      tags: ['installation', 'errors', 'troubleshooting'],
      views: 892,
      author: 'Support Team',
      created_at: '2025-05-10T09:00:00Z',
      updated_at: '2025-05-18T11:00:00Z'
    },
    {
      id: 3,
      title: 'Understanding Your Invoice',
      content: 'A complete guide to understanding the different sections of your invoice...',
      category: 'billing',
      tags: ['billing', 'invoice', 'payment'],
      views: 567,
      author: 'Finance Team',
      created_at: '2025-05-08T15:00:00Z',
      updated_at: '2025-05-08T15:00:00Z'
    },
    {
      id: 4,
      title: 'AMC Renewal Process',
      content: 'Learn how to renew your Annual Maintenance Contract...',
      category: 'billing',
      tags: ['amc', 'renewal', 'maintenance'],
      views: 445,
      author: 'Admin',
      created_at: '2025-05-05T12:00:00Z',
      updated_at: '2025-05-19T10:00:00Z'
    },
    {
      id: 5,
      title: 'Remote Support Setup Guide',
      content: 'How to set up and use remote support feature...',
      category: 'technical',
      tags: ['remote', 'support', 'setup'],
      views: 678,
      author: 'Tech Team',
      created_at: '2025-05-01T08:00:00Z',
      updated_at: '2025-05-15T16:00:00Z'
    },
  ];

  const displayCategories = categories.length > 1 ? categories : sampleCategories;
  const displayArticles = articles.length > 0 ? articles : sampleArticles;

  const filteredArticles = displayArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          article.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (article.tags || []).some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const resetForm = () => {
    setNewArticle({ title: '', content: '', category: 'general', tags: [] });
    setTagInput('');
    setEditingArticle(null);
  };

  const handleCreateArticle = async () => {
    if (!newArticle.title || !newArticle.content) {
      toast.error('Please fill in required fields');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API}/api/support/knowledge-base`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newArticle)
      });

      if (response.ok) {
        const data = await response.json();
        const newArticleWithDefaults = {
          ...newArticle,
          id: data.article?.id || Date.now(),
          views: 0,
          author: 'You',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setArticles(prev => [newArticleWithDefaults, ...prev]);
        updateCategories([newArticleWithDefaults, ...articles]);
        toast.success('Article created successfully');
        setShowCreateModal(false);
        resetForm();
      } else {
        toast.error('Failed to create article');
      }
    } catch (error) {
      // Even if API fails, add locally for demo
      const newArticleWithDefaults = {
        ...newArticle,
        id: Date.now(),
        views: 0,
        author: 'You',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setArticles(prev => [newArticleWithDefaults, ...prev]);
      updateCategories([newArticleWithDefaults, ...articles]);
      toast.success('Article created successfully');
      setShowCreateModal(false);
      resetForm();
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateArticle = async () => {
    if (!newArticle.title || !newArticle.content) {
      toast.error('Please fill in required fields');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API}/api/support/knowledge-base/${editingArticle.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newArticle)
      });

      if (response.ok) {
        setArticles(prev => prev.map(a => 
          a.id === editingArticle.id 
            ? { ...a, ...newArticle, updated_at: new Date().toISOString() }
            : a
        ));
        toast.success('Article updated successfully');
        setShowCreateModal(false);
        setSelectedArticle(null);
        resetForm();
      } else {
        toast.error('Failed to update article');
      }
    } catch (error) {
      // Update locally anyway
      setArticles(prev => prev.map(a => 
        a.id === editingArticle.id 
          ? { ...a, ...newArticle, updated_at: new Date().toISOString() }
          : a
      ));
      toast.success('Article updated successfully');
      setShowCreateModal(false);
      setSelectedArticle(null);
      resetForm();
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    try {
      await fetch(`${API}/api/support/knowledge-base/${articleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(prev => prev.filter(a => a.id !== articleId));
      setSelectedArticle(null);
      toast.success('Article deleted');
    } catch (error) {
      setArticles(prev => prev.filter(a => a.id !== articleId));
      setSelectedArticle(null);
      toast.success('Article deleted');
    }
  };

  const handleEditArticle = (article) => {
    setEditingArticle(article);
    setNewArticle({
      title: article.title,
      content: article.content,
      category: article.category,
      tags: article.tags || []
    });
    setSelectedArticle(null);
    setShowCreateModal(true);
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!newArticle.tags.includes(tagInput.trim())) {
        setNewArticle({ ...newArticle, tags: [...newArticle.tags, tagInput.trim()] });
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setNewArticle({ ...newArticle, tags: newArticle.tags.filter(t => t !== tagToRemove) });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-primary" />
            Knowledge Base
          </h1>
          <p className="text-muted-foreground">Manage support articles and documentation</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="support-btn-primary flex items-center gap-2"
          data-testid="create-article-btn"
        >
          <Plus className="w-4 h-4" />
          New Article
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search articles..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="support-input pl-11 text-base"
          data-testid="kb-search"
        />
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Categories Sidebar */}
        <div className="lg:col-span-1">
          <div className="support-card p-0 overflow-hidden">
            <div className="p-4 border-b border-border">
              <h3 className="font-semibold flex items-center gap-2">
                <FolderOpen className="w-4 h-4 text-primary" />
                Categories
              </h3>
            </div>
            <div className="divide-y divide-border/50">
              {displayCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`w-full flex items-center justify-between p-3 transition-colors ${
                    selectedCategory === category.id ? 'bg-primary/10 text-primary' : 'hover:bg-secondary/50'
                  }`}
                  data-testid={`category-${category.id}`}
                >
                  <span className="text-sm">{category.name}</span>
                  <span className="text-xs bg-secondary px-2 py-0.5 rounded-full">{category.count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Articles Grid */}
        <div className="lg:col-span-3">
          {selectedArticle ? (
            // Article Detail View
            <div className="support-card">
              <button
                onClick={() => setSelectedArticle(null)}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
                Back to articles
              </button>
              
              <h2 className="text-xl font-bold mb-2">{selectedArticle.title}</h2>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {selectedArticle.author}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Updated {formatDate(selectedArticle.updated_at)}
                </span>
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  {selectedArticle.views} views
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {(selectedArticle.tags || []).map((tag, index) => (
                  <span key={index} className="flex items-center gap-1 text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full">
                    <Tag className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
              </div>

              <div className="prose prose-sm max-w-none text-foreground">
                <p className="whitespace-pre-line">{selectedArticle.content}</p>
              </div>

              <div className="flex gap-2 mt-6 pt-6 border-t border-border">
                <button 
                  onClick={() => handleEditArticle(selectedArticle)}
                  className="support-btn-secondary flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
                <button 
                  onClick={() => handleDeleteArticle(selectedArticle.id)}
                  className="support-btn-secondary flex items-center gap-2 text-red-400 hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          ) : (
            // Articles List
            <div className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : filteredArticles.length > 0 ? (
                filteredArticles.map((article) => (
                  <div
                    key={article.id}
                    onClick={() => setSelectedArticle(article)}
                    className="support-card cursor-pointer hover:border-primary/50 transition-all"
                    data-testid={`article-${article.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-5 h-5 text-primary shrink-0" />
                          <h3 className="font-semibold truncate">{article.title}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                          {article.content.substring(0, 150)}...
                        </p>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                          <span className="bg-secondary px-2 py-1 rounded capitalize">{article.category}</span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {article.views}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(article.updated_at)}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                    </div>
                  </div>
                ))
              ) : (
                <div className="support-card text-center py-12">
                  <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No articles found</p>
                  <button 
                    onClick={() => {
                      resetForm();
                      setShowCreateModal(true);
                    }}
                    className="support-btn-primary mt-4"
                  >
                    Create First Article
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Article Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
  <div
    className="
      bg-card
      border border-border
      rounded-xl
      w-full
      max-w-3xl
      max-h-[95vh]
      flex
      flex-col
      shadow-xl
      animate-fade-in
    "
    data-testid="create-article-modal"
  >
    {/* Header */}
    <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
      <h2 className="text-lg sm:text-xl font-bold">
        {editingArticle ? "Edit Article" : "Create New Article"}
      </h2>

      <button
        onClick={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        className="p-2 rounded-lg hover:bg-secondary"
      >
        <X className="w-5 h-5" />
      </button>
    </div>

    {/* Scrollable Content */}
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">
          Title *
        </label>

        <input
          type="text"
          value={newArticle.title}
          onChange={(e) =>
            setNewArticle({
              ...newArticle,
              title: e.target.value,
            })
          }
          placeholder="Article title"
          className="support-input w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          Category
        </label>

        <select
          value={newArticle.category}
          onChange={(e) =>
            setNewArticle({
              ...newArticle,
              category: e.target.value,
            })
          }
          className="support-input w-full"
        >
          <option value="general">General</option>
          <option value="technical">Technical</option>
          <option value="billing">Billing</option>
          <option value="installation">Installation</option>
          <option value="troubleshooting">Troubleshooting</option>
          <option value="faq">FAQ</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          Content *
        </label>

        <textarea
          value={newArticle.content}
          onChange={(e) =>
            setNewArticle({
              ...newArticle,
              content: e.target.value,
            })
          }
          placeholder="Write your article content..."
          rows={10}
          className="
            support-input
            resize-none
            w-full
            min-h-[200px]
            sm:min-h-[300px]
          "
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          Tags
        </label>

        <div className="flex flex-wrap gap-2 mb-3">
          {newArticle.tags.map((tag, i) => (
            <span
              key={i}
              className="
                px-2 py-1
                bg-primary/20
                text-primary
                text-xs
                rounded-full
                flex items-center gap-1
              "
            >
              {tag}

              <button
                onClick={() => handleRemoveTag(tag)}
                className="hover:text-red-400"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>

        <input
          type="text"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleAddTag}
          placeholder="Type a tag and press Enter"
          className="support-input w-full"
        />
      </div>
    </div>

    {/* Footer */}
    <div
      className="
        border-t border-border
        p-4
        shrink-0
        flex
        flex-col
        sm:flex-row
        gap-3
      "
    >
      <button
        onClick={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        className="
          w-full
          sm:flex-1
          support-btn-secondary
        "
      >
        Cancel
      </button>

      <button
        onClick={
          editingArticle
            ? handleUpdateArticle
            : handleCreateArticle
        }
        disabled={creating}
        className="
          w-full
          sm:flex-1
          support-btn-primary
          flex
          items-center
          justify-center
          gap-2
        "
      >
        {creating ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Plus className="w-4 h-4" />
        )}

        {editingArticle
          ? "Update Article"
          : "Create Article"}
      </button>
    </div>
  </div>
</div>
      )}
    </div>
  );
}
